import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, Alert, Modal, Form, Row, Col, Spinner, InputGroup, Pagination, Collapse, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaUsers, FaPlus, FaEdit, FaTrash, FaCalendarAlt, FaSearch, FaFilter, FaSort, FaSortUp, FaSortDown, FaChevronDown, FaChevronUp, FaTimes, FaEye, FaEyeSlash, FaPause, FaPlay } from 'react-icons/fa';
import { useApi } from '../context/ApiContext';
import { useAuth } from '../hooks/useAuth';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const Users = () => {
  const { apiCall } = useApi();
  const { user } = useAuth();
  
  // Role-based access
  const isAdmin = user?.role === 'admin';
  const isVisaOffice = user?.role === 'visa_office';
  const isRegularUser = user?.role === 'user';
  
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [pausingUserId, setPausingUserId] = useState(null);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    username: '',
    appointment_city: '',
    visa_class: '',
    appointment_status: 'all', // all, has_appointment, no_appointment
    user_status: 'all', // all, active, paused, inactive
    date_range_status: 'all', // all, has_ranges, no_ranges
    applicant_count: 'all' // all, 1, 2, 3, 4+
  });

  // Language state for security questions
  const [language, setLanguage] = useState('tr'); // 'tr' or 'en'

  // Sort state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [paginatedUsers, setPaginatedUsers] = useState([]);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    appointment_city: '',
    visa_type: '',
    question1: '',
    question1Index: -1,
    answer1: '',
    question2: '',
    question2Index: -1,
    answer2: '',
    question3: '',
    question3Index: -1,
    answer3: ''
  });

  // Date ranges state
  const [dateRanges, setDateRanges] = useState([]);
  const [newDateRange, setNewDateRange] = useState({
    start_date: '',
    end_date: ''
  });
  const [editingDateRange, setEditingDateRange] = useState(null);
  const [editDateRange, setEditDateRange] = useState({
    start_date: '',
    end_date: ''
  });

  // Date ranges modal state
  const [showDateRangesModal, setShowDateRangesModal] = useState(false);
  const [dateRangesUser, setDateRangesUser] = useState(null);
  const [dateRangesLoading, setDateRangesLoading] = useState(false);
  const [selectedRangeForAction, setSelectedRangeForAction] = useState(null);
  const [calendarActiveStartDate, setCalendarActiveStartDate] = useState(new Date());
  
  // Refs for scrolling within modal
  const addFormRef = React.useRef(null);
  const calendarTopRef = React.useRef(null);

  const visaClasses = ['B1/B2', 'F-1', 'J-1'];

  // Security questions from the provided JSON
  const securityQuestions = {
    tr: {
      question1: [
        "Annenizin kızlık soyadı nedir?",
        "İlk/şimdiki/en sevdiğiniz evcil hayvanınızın adı neydi?",
        "İlk arabanız neydi?",
        "Hangi ilkokula gittiniz?",
        "Doğduğunuz kasabanın/şehrin adı nedir?"
      ],
      question2: [
        "Büyüdüğünüz yolun/sokağın adı nedir?",
        "En az sevdiğiniz yemek nedir?",
        "İlk çalıştığınız şirket hangisiydi?",
        "En sevdiğiniz yemek nedir?",
        "Hangi liseye gittiniz?"
      ],
      question3: [
        "Eşinizle nerede tanıştınız?",
        "Kardeşinizin ikinci adı ne?",
        "Çocukluk kahramanınız kimdi?",
        "İlk işiniz hangi şehir veya kasabadaydı?",
        "Başvurduğunuz ancak kaydolmadığınız bir kolejin adı nedir?"
      ]
    },
    en: {
      question1: [
        "What is your mother's maiden name?",
        "What was the name of your first/current/favorite pet?",
        "What was your first car?",
        "What elementary school did you attend?",
        "What is the name of the town/city where you were born?"
      ],
      question2: [
        "What is the name of the road/street you grew up on?",
        "What is your least favorite food?",
        "What was the first company that you worked for?",
        "What is your favorite food?",
        "What high school did you attend?"
      ],
      question3: [
        "Where did you meet your spouse?",
        "What is your sibling's middle name?",
        "Who was your childhood hero?",
        "In what city or town was your first job?",
        "What is the name of a college you applied to but didn't attend?"
      ]
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Apply filters whenever users or filters change
  useEffect(() => {
    applyFilters();
  }, [users, filters, sortConfig]);

  // Apply pagination when currentPage or itemsPerPage changes
  useEffect(() => {
    applyPagination();
  }, [currentPage, itemsPerPage, filteredUsers]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortConfig]);

  // Mevcut kullanıcı yüklenirken DB'den form verilerini sadece modal açılışında doldur.
  // Dil değişiminde bu effect tetiklenmez; dil değişince alttaki çeviri effect'i çalışır.
  useEffect(() => {
    if (currentUser && currentUser.security_questions && showModal) {
      try {
        const securityQuestionsData = currentUser.security_questions || {};
        const questions = Object.keys(securityQuestionsData);
        const answers = Object.values(securityQuestionsData);

        const categorizedQuestions = { question1: '', question2: '', question3: '' };
        const categorizedAnswers = { answer1: '', answer2: '', answer3: '' };
        const categorizedIndexes = { question1Index: -1, question2Index: -1, question3Index: -1 };

        // DB'de aynı cevapla her iki dildeki soru da saklanıyorsa, aktif dilin listesinde
        // doğru indeksi bulabilmek için diğer dildeki (unique) soruya bakıyoruz.
        const otherLangForLoad = language === 'tr' ? 'en' : 'tr';

        questions.forEach((question, index) => {
          const result = determineQuestionCategory(question, language);
          if (result.found && result.category) {
            const indexKey = `${result.category}Index`;
            if (categorizedIndexes[indexKey] !== -1) return;
            const categoryList = securityQuestions[language]?.[result.category] || [];

            // Önce aktif dildeki listede exact match ara
            let resolvedIdx = categoryList.indexOf(result.matchedQuestion);

            // Bulunamazsa diğer dildeki aynı sorunun index'ini kullan
            if (resolvedIdx === -1) {
              const otherList = securityQuestions[otherLangForLoad]?.[result.category] || [];
              const counterpart = questions.find(q => otherList.includes(q));
              if (counterpart) {
                resolvedIdx = otherList.indexOf(counterpart);
              }
            }

            categorizedQuestions[result.category] = result.matchedQuestion;
            categorizedAnswers[result.category.replace('question', 'answer')] = answers[index];
            categorizedIndexes[indexKey] = resolvedIdx;
          }
        });

        setFormData(prev => ({
          ...prev,
          question1: categorizedQuestions.question1,
          question1Index: categorizedIndexes.question1Index,
          answer1: categorizedAnswers.answer1,
          question2: categorizedQuestions.question2,
          question2Index: categorizedIndexes.question2Index,
          answer2: categorizedAnswers.answer2,
          question3: categorizedQuestions.question3,
          question3Index: categorizedIndexes.question3Index,
          answer3: categorizedAnswers.answer3
        }));
      } catch (e) {
        console.error('Error updating form data:', e);
      }
    }
  }, [currentUser, showModal]);

  // Dil değiştiğinde, halihazırda seçili dropdown sıralarını (index) yeni dilin
  // karşılığına çevir. Cevaplar ve indexler korunur; sadece soru metinleri güncellenir.
  // Kaydet'e basıldığında bu en son hal DB'ye yazılır.
  useEffect(() => {
    if (!showModal) return;
    setFormData(prev => {
      const next = { ...prev };
      ['question1', 'question2', 'question3'].forEach(cat => {
        const idx = prev[`${cat}Index`];
        if (idx != null && idx >= 0) {
          const text = securityQuestions[language]?.[cat]?.[idx];
          if (text) next[cat] = text;
        } else {
          next[cat] = '';
        }
      });
      return next;
    });
  }, [language]);

  // Set calendar to first date range when date ranges are loaded
  useEffect(() => {
    if (dateRanges.length > 0) {
      setCalendarActiveStartDate(new Date(dateRanges[0].start_date));
    } else {
      setCalendarActiveStartDate(new Date());
    }
  }, [dateRanges]);

  // Get unique cities from users
  const getUniqueCities = () => {
    const cities = users
      .map(user => user.appointment_city)
      .filter(city => city && city.trim() !== '')
      .filter((city, index, self) => self.indexOf(city) === index)
      .sort();
    return cities;
  };

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.username) count++;
    if (filters.appointment_city) count++;
    if (filters.visa_class) count++;
    if (filters.appointment_status !== 'all') count++;
    if (filters.user_status !== 'all') count++;
    if (filters.date_range_status !== 'all') count++;
    if (filters.applicant_count !== 'all') count++;
    return count;
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiCall('GET', '/api/visa-users');
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    // Filter by username
    if (filters.username) {
      filtered = filtered.filter(user => 
        user.username?.toLowerCase().includes(filters.username.toLowerCase())
      );
    }

    // Filter by appointment city
    if (filters.appointment_city) {
      filtered = filtered.filter(user => user.appointment_city === filters.appointment_city);
    }

    // Filter by visa class
    if (filters.visa_class) {
      filtered = filtered.filter(user => user.visa_class === filters.visa_class);
    }

    // Filter by appointment status
    if (filters.appointment_status === 'has_appointment') {
      filtered = filtered.filter(user => user.appointment_date);
    } else if (filters.appointment_status === 'no_appointment') {
      filtered = filtered.filter(user => !user.appointment_date);
    }

    // Filter by user status (is_active)
    if (filters.user_status === 'active') {
      filtered = filtered.filter(user => user.is_active === 1);
    } else if (filters.user_status === 'paused') {
      filtered = filtered.filter(user => user.is_active === 2);
    } else if (filters.user_status === 'inactive') {
      filtered = filtered.filter(user => user.is_active === 0);
    }

    // Filter by date range status (appointment_city as indicator)
    if (filters.date_range_status === 'has_ranges') {
      filtered = filtered.filter(user => user.dateRangeCount && user.dateRangeCount > 0);
    } else if (filters.date_range_status === 'no_ranges') {
      filtered = filtered.filter(user => !user.dateRangeCount || user.dateRangeCount === 0);
    }

    // Filter by applicant count
    if (filters.applicant_count !== 'all') {
      filtered = filtered.filter(user => {
        const count = getApplicantCount(user.applicant_name);
        if (filters.applicant_count === '4+') {
          return count >= 4;
        }
        return count === parseInt(filters.applicant_count);
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        // Special handling for applicant_count
        if (sortConfig.key === 'applicant_count') {
          aValue = getApplicantCount(a.applicant_name);
          bValue = getApplicantCount(b.applicant_name);
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        // Handle null/undefined values
        if (!aValue && !bValue) return 0;
        if (!aValue) return sortConfig.direction === 'asc' ? 1 : -1;
        if (!bValue) return sortConfig.direction === 'asc' ? -1 : 1;

        // Convert to string for comparison (except dates and numbers)
        if (sortConfig.key === 'appointment_date') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (sortConfig.key === 'applicant_count') {
          // Already numbers, no conversion needed
        } else {
          aValue = aValue.toString().toLowerCase();
          bValue = bValue.toString().toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredUsers(filtered);
    applyPagination(filtered);
  };

  const applyPagination = (dataToPage = null) => {
    const data = dataToPage || filteredUsers;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = data.slice(startIndex, endIndex);
    setPaginatedUsers(paginated);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      username: '',
      appointment_city: '',
      visa_class: '',
      appointment_status: 'all',
      user_status: 'all',
      date_range_status: 'all',
      applicant_count: 'all'
    });
  };

  // Load user appointments
  const loadUserAppointments = async (userId) => {
    try {
      setDateRangesLoading(true);
      const response = await apiCall('GET', `/api/user-appointments/${userId}`);
      setDateRanges(response.data || []);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setDateRanges([]);
    } finally {
      setDateRangesLoading(false);
    }
  };

  // Calendar helper functions
  const getDateRangeForDate = (date) => {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    
    return dateRanges.find(range => {
      const start = new Date(range.start_date);
      const end = new Date(range.end_date);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      
      return targetDate >= start && targetDate <= end;
    });
  };

  const getTileClassName = ({ date, view }) => {
    if (view !== 'month') return null;
    
    const range = getDateRangeForDate(date);
    if (range) {
      // Check if this is the selected range
      if (selectedRangeForAction && range.id === selectedRangeForAction.id) {
        return 'has-date-range selected-range';
      }
      return 'has-date-range';
    }
    return null;
  };

  const handleCalendarDateClick = (date) => {
    const range = getDateRangeForDate(date);
    if (range) {
      // If clicked on an existing range, select it for editing
      setSelectedRangeForAction(range);
      // Scroll to top of calendar within modal
      if (calendarTopRef.current) {
        calendarTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // If clicked on an empty date, prepare to add new range
      const clickedDate = new Date(date);
      const formattedDate = clickedDate.toISOString().split('T')[0];
      setNewDateRange({
        start_date: formattedDate,
        end_date: formattedDate
      });
      // Scroll to the add form within modal
      setTimeout(() => {
        if (addFormRef.current) {
          addFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Add new date range
  const addDateRange = async () => {
    if (!newDateRange.start_date || !newDateRange.end_date) {
      alert('Lütfen başlangıç ve bitiş tarihlerini seçin');
      return;
    }

    if (new Date(newDateRange.end_date) < new Date(newDateRange.start_date)) {
      alert('Bitiş tarihi başlangıç tarihinden önce olamaz');
      return;
    }

    try {
      setDateRangesLoading(true);
      
      await apiCall('POST', '/api/user-appointments', {
        visa_user_id: dateRangesUser.id,
        start_date: newDateRange.start_date,
        end_date: newDateRange.end_date,
        is_active: 1  // Varsayılan olarak aktif
      });
      
      setNewDateRange({ start_date: '', end_date: '' });
      await loadUserAppointments(dateRangesUser.id);
      
      // Update the user's dateRangeCount in the main list without refreshing
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === dateRangesUser.id 
            ? { ...user, dateRangeCount: (user.dateRangeCount || 0) + 1 }
            : user
        )
      );
    } catch (err) {
      alert(err.message || 'Tarih aralığı eklenirken hata oluştu');
    } finally {
      setDateRangesLoading(false);
    }
  };

  // Remove date range
  const removeDateRange = async (appointmentId) => {
    try {
      setDateRangesLoading(true);
      await apiCall('DELETE', `/api/user-appointments/${appointmentId}`);
      await loadUserAppointments(dateRangesUser.id);
      
      // Update the user's dateRangeCount in the main list without refreshing
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === dateRangesUser.id 
            ? { ...user, dateRangeCount: Math.max(0, (user.dateRangeCount || 1) - 1) }
            : user
        )
      );
    } catch (err) {
      alert(err.message || 'Tarih aralığı silinirken hata oluştu');
    } finally {
      setDateRangesLoading(false);
    }
  };

  // Start editing date range
  const startEditDateRange = (range) => {
    setEditingDateRange(range.id);
    setEditDateRange({
      start_date: range.start_date,
      end_date: range.end_date
    });
  };

  // Cancel editing date range
  const cancelEditDateRange = () => {
    setEditingDateRange(null);
    setEditDateRange({ start_date: '', end_date: '' });
  };

  // Save edited date range
  const saveEditDateRange = async () => {
    if (!editDateRange.start_date || !editDateRange.end_date) {
      alert('Lütfen başlangıç ve bitiş tarihlerini seçin');
      return;
    }

    if (new Date(editDateRange.end_date) < new Date(editDateRange.start_date)) {
      alert('Bitiş tarihi başlangıç tarihinden önce olamaz');
      return;
    }

    try {
      setDateRangesLoading(true);
      await apiCall('PUT', `/api/user-appointments/${editingDateRange}`, {
        start_date: editDateRange.start_date,
        end_date: editDateRange.end_date
      });
      
      setEditingDateRange(null);
      setEditDateRange({ start_date: '', end_date: '' });
      await loadUserAppointments(dateRangesUser.id);
    } catch (err) {
      alert(err.message || 'Tarih aralığı güncellenirken hata oluştu');
    } finally {
      setDateRangesLoading(false);
    }
  };

  // Pagination helper functions
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, filteredUsers.length);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Find question in specific category using fuzzy matching
  const findMatchingQuestionInCategory = (dbQuestion, categoryQuestions) => {
    if (!dbQuestion || !categoryQuestions) return '';
    
    // First try exact match
    if (categoryQuestions.includes(dbQuestion)) {
      return dbQuestion;
    }
    
    // Then try fuzzy match — only allow if lengths are close (prevents substring false-positives)
    const match = categoryQuestions.find(availableQuestion => {
      const dbLower = dbQuestion.toLowerCase().trim();
      const availableLower = availableQuestion.toLowerCase().trim();
      const lenRatio = Math.min(dbLower.length, availableLower.length) / Math.max(dbLower.length, availableLower.length);
      if (lenRatio < 0.85) return false;
      return dbLower.includes(availableLower) ||
             availableLower.includes(dbLower) ||
             dbLower === availableLower;
    });
    
    return match || '';
  };

  // Determine which category a question belongs to
  const determineQuestionCategory = (dbQuestion, language) => {
    const categories = ['question1', 'question2', 'question3'];
    
    for (const category of categories) {
      const categoryQuestions = securityQuestions[language]?.[category] || [];
      const match = findMatchingQuestionInCategory(dbQuestion, categoryQuestions);
      if (match) {
        return { category, matchedQuestion: match, found: true };
      }
    }
    
    // If no match found, return null to indicate no match
    return { category: null, matchedQuestion: '', found: false };
  };

  // Detect language of security questions
  const detectQuestionLanguage = (questions) => {
    if (!questions || questions.length === 0) return 'tr';
    
    // Flatten all Turkish questions into one array
    const allTurkishQuestions = [
      ...securityQuestions.tr.question1,
      ...securityQuestions.tr.question2,
      ...securityQuestions.tr.question3
    ];
    
    // Flatten all English questions into one array  
    const allEnglishQuestions = [
      ...securityQuestions.en.question1,
      ...securityQuestions.en.question2,
      ...securityQuestions.en.question3
    ];
    
    // Check if any question exists in Turkish list
    const turkishMatches = questions.some(question => {
      const match = allTurkishQuestions.includes(question);
      return match;
    });
    
    // Check if any question exists in English list  
    const englishMatches = questions.some(question => {
      const match = allEnglishQuestions.includes(question);
      return match;
    });
    
    // Return the detected language, default to Turkish
    if (englishMatches && !turkishMatches) {
      return 'en';
    }
    return 'tr';
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <FaSort className="text-muted ms-1" size={12} />;
    }
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="text-primary ms-1" size={12} />
      : <FaSortDown className="text-primary ms-1" size={12} />;
  };

  const handleShowModal = (user = null) => {
    if (user) {
      setCurrentUser(user);
      
      // Get security questions - now comes as JSON object directly
      const securityQuestionsData = user.security_questions || {};
      
      // Extract questions from the database format
      const questions = Object.keys(securityQuestionsData);
      
      // Detect and set language based on questions
      const detectedLanguage = detectQuestionLanguage(questions);
      setLanguage(detectedLanguage);
      
      // Set basic form data (questions will be set by useEffect)
      setFormData({
        username: user.username || '',
        password: user.password || '',
        appointment_city: user.appointment_city || '',
        visa_type: user.visa_class || '',
        question1: '',
        question1Index: -1,
        answer1: '',
        question2: '',
        question2Index: -1,
        answer2: '',
        question3: '',
        question3Index: -1,
        answer3: ''
      });
      
    } else {
      setCurrentUser(null);
      // Reset to Turkish for new users
      setLanguage('tr');
      setFormData({
        username: '',
        password: '',
        appointment_city: '',
        visa_type: '',
        question1: '',
        question1Index: -1,
        answer1: '',
        question2: '',
        question2Index: -1,
        answer2: '',
        question3: '',
        question3Index: -1,
        answer3: ''
      });
    }

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentUser(null);
    setShowPassword(false);
    setFormData({
      username: '',
      password: '',
      appointment_city: '',
      visa_type: '',
      question1: '',
      question1Index: -1,
      answer1: '',
      question2: '',
      question2Index: -1,
      answer2: '',
      question3: '',
      question3Index: -1,
      answer3: ''
    });
    // Reset date ranges
    setDateRanges([]);
    setNewDateRange({ start_date: '', end_date: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Soru dropdown'ları için: option value'ları artık index olduğundan direkt değeri kullanıyoruz.
  // Aynı metin birden fazla kez geçen TR sorularında bile doğru sırayı ayırt edebilmek için
  // index bazlı state tutuyoruz.
  const handleQuestionChange = (e) => {
    const { name, value } = e.target;
    const optionIndex = value === '' ? -1 : parseInt(value, 10);
    const text = optionIndex >= 0
      ? (securityQuestions[language]?.[name]?.[optionIndex] || '')
      : '';
    setFormData(prev => ({
      ...prev,
      [name]: text,
      [`${name}Index`]: optionIndex
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Seçilen sorunun dropdown'daki sırasına göre hem aktif dildeki hem de karşı
      // dildeki metnini alır. Duplike metin olan durumlarda doğru eşleşme için metin
      // yerine indeks kullanılır.
      const otherLang = language === 'tr' ? 'en' : 'tr';

      // Yemek sorularının her iki versiyonunun (en az / en sevdiği) index'leri
      const FOOD_QUESTION_INDEXES = [1, 3]; // question2 içinde least(1) ve favorite(3)

      const addQuestionByIndex = (sqObj, category, idx, answer) => {
        if (idx == null || idx < 0 || !answer) return;
        const currentText = securityQuestions[language]?.[category]?.[idx];
        const otherText = securityQuestions[otherLang]?.[category]?.[idx];
        if (currentText) sqObj[currentText] = answer;
        if (otherText && otherText !== currentText) sqObj[otherText] = answer;

        // Yemek sorusu seçildiyse diğer yemek sorusunun 4 versiyonunu da kaydet
        if (category === 'question2' && FOOD_QUESTION_INDEXES.includes(idx)) {
          FOOD_QUESTION_INDEXES.forEach(foodIdx => {
            if (foodIdx === idx) return;
            const trText = securityQuestions['tr']?.[category]?.[foodIdx];
            const enText = securityQuestions['en']?.[category]?.[foodIdx];
            if (trText) sqObj[trText] = answer;
            if (enText) sqObj[enText] = answer;
          });
        }
      };

      // Prepare security questions in database format
      const securityQuestionsPayload = {};
      addQuestionByIndex(securityQuestionsPayload, 'question1', formData.question1Index, formData.answer1);
      addQuestionByIndex(securityQuestionsPayload, 'question2', formData.question2Index, formData.answer2);
      addQuestionByIndex(securityQuestionsPayload, 'question3', formData.question3Index, formData.answer3);

      // Determine country code based on visa type and city
      let countryCode = '';
      const visaType = formData.visa_type;
      const city = formData.appointment_city;
      
      if (visaType === 'B1/B2') {
        if (city === 'Istanbul') {
          countryCode = 'tri';
        } else if (city === 'Ankara') {
          countryCode = 'tr2';
        }
      } else if (visaType === 'F-1') {
        if (city === 'Istanbul') {
          countryCode = 'f1i';
        } else if (city === 'Ankara') {
          countryCode = 'f1a';
        }
      } else if (visaType === 'J-1') {
        // J-1 için her iki şehir de f1i kullanır
        countryCode = 'f1i';
      }

      // Calculate requestCount based on country
      let requestCount = null;
      
      console.log('==== REQUEST COUNT CALCULATION ====');
      console.log('Current user:', currentUser);
      console.log('Selected country:', countryCode);
      console.log('🔄 Calling backend to calculate requestCount for country:', countryCode);
      
      // Always calculate requestCount from backend (for both new and existing users)
      try {
        const response = await apiCall('POST', '/api/visa-users/calculate-request-count', {
          country: countryCode
        });
        
        requestCount = response.requestCount;
        console.log('✅ Backend returned requestCount:', requestCount);
        console.log('📊 Details:', response.details);
      } catch (err) {
        console.error('❌ Error calculating requestCount:', err);
        requestCount = currentUser?.requestCount ?? 1000; // Use existing or fallback to default
        console.log('⚠️ Using fallback:', requestCount);
      }
      
      console.log('==== END REQUEST COUNT CALCULATION ====');

      // Prepare data to send to backend
      const dataToSend = {
        username: formData.username,
        password: formData.password,
        appointment_city: formData.appointment_city || null,
        visa_class: formData.visa_type || null,
        country: countryCode || null,
        security_questions: Object.keys(securityQuestionsPayload).length > 0 ? securityQuestionsPayload : null,
        requestCount: requestCount
      };

      console.log('📤 Sending data to backend:', dataToSend);
      console.log('📋 Form data visa_type:', formData.visa_type);

      if (currentUser) {
        await apiCall('PUT', `/api/visa-users/${currentUser.id}`, dataToSend);
      } else {
        await apiCall('POST', '/api/visa-users', dataToSend);
      }
      handleCloseModal();
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await apiCall('DELETE', `/api/visa-users/${currentUser.id}`);
      setShowDeleteModal(false);
      setCurrentUser(null);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTogglePause = async (userId, currentStatus) => {
    setPausingUserId(userId); // Loading başlat
    try {
      const response = await apiCall('PUT', `/api/visa-users/${userId}/toggle-pause`);
      if (response.success) {
        // Sadece ilgili kullanıcının durumunu güncelle (sayfa yenilenmeden)
        const newStatus = response.data.is_active;
        
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId 
              ? { ...user, is_active: newStatus }
              : user
          )
        );
        
        // Filtrelenmiş listeyi de güncelle
        setFilteredUsers(prevFiltered =>
          prevFiltered.map(user =>
            user.id === userId
              ? { ...user, is_active: newStatus }
              : user
          )
        );
      }
    } catch (err) {
      setError(err.message || 'Durum değiştirilemedi');
    } finally {
      setPausingUserId(null); // Loading bitir
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const getUserStatusBadge = (isActive) => {
    if (isActive === 1) {
      return { bg: 'success', text: 'Aktif' };
    } else if (isActive === 2) {
      return { bg: 'warning', text: 'Duraklatıldı' };
    } else {
      return { bg: 'secondary', text: 'Pasif' };
    }
  };

  const getApplicantCount = (applicantName) => {
    if (!applicantName || applicantName.trim() === '') return 0;
    // Count "|" characters and add 1
    const pipeCount = (applicantName.match(/\|/g) || []).length;
    return pipeCount + 1;
  };

  const formatApplicantNames = (applicantName) => {
    if (!applicantName || applicantName.trim() === '') return [];
    // Split by | and trim each name
    return applicantName.split('|').map(name => name.trim()).filter(name => name !== '');
  };

  const getVisaClassBadge = (visaClass) => {
    if (!visaClass) return <span className="text-muted">-</span>;
    
    const colors = {
      'B1/B2': 'primary',
      'F-1': 'info',
      'J-1': 'info'
    };
    
    return <Badge bg={colors[visaClass] || 'secondary'}>{visaClass}</Badge>;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Yükleniyor...</span>
          </Spinner>
          <p className="mt-3 text-muted">Kullanıcılar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 d-flex justify-content-between align-items-start">
        <div>
          <h1 className="h3 mb-2 d-flex align-items-center">
            <FaUsers className="me-2 text-primary" />
            {isAdmin ? 'Vize Kullanıcıları' : 
             isVisaOffice ? 'Firma Müşterileri' : 
             'Vize Başvurularım'}
          </h1>
          <p className="text-muted mb-0">
            {isAdmin ? 'Sistemdeki tüm vize kullanıcılarını yönetin' :
             isVisaOffice ? 'Danışmanlık firmanızın müşterilerini yönetin' :
             'Kendi vize başvurularınızı yönetin'}
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => handleShowModal()}
          className="d-flex align-items-center gap-2"
        >
          <FaPlus size={14} />
          {isRegularUser ? 'Yeni Başvuru' : 'Yeni Kullanıcı'}
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Compact Filter Bar */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">
            {/* Kullanıcı Adı Arama */}
            <Col md={4}>
              <Form.Label className="small text-muted mb-1">Kullanıcı Adı</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Kullanıcı adı ara..."
                  value={filters.username}
                  onChange={(e) => handleFilterChange('username', e.target.value)}
                />
              </InputGroup>
            </Col>

            {/* Filtreler Butonu */}
            <Col md={3}>
              <Button
                variant={getActiveFilterCount() > 0 ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="d-flex align-items-center gap-2 w-100"
              >
                <FaFilter />
                Filtreler
                {getActiveFilterCount() > 0 && (
                  <Badge bg="light" text="primary" className="ms-1">
                    {getActiveFilterCount()}
                  </Badge>
                )}
                {showFilters ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
              </Button>
            </Col>

            {/* Temizle Butonu */}
            {getActiveFilterCount() > 0 && (
              <Col md={2}>
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  onClick={clearFilters}
                  className="w-100"
                >
                  <FaTimes className="me-1" />
                  Temizle
                </Button>
              </Col>
            )}
          </Row>

          {/* Collapsible Advanced Filters */}
          <Collapse in={showFilters}>
            <div className="mt-3 pt-3 border-top">
              <Row className="g-3">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">Randevu Şehri</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.appointment_city}
                      onChange={(e) => handleFilterChange('appointment_city', e.target.value)}
                    >
                      <option value="">Tüm Şehirler</option>
                      {getUniqueCities().map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">Vize Sınıfı</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.visa_class}
                      onChange={(e) => handleFilterChange('visa_class', e.target.value)}
                    >
                      <option value="">Tüm Sınıflar</option>
                      {visaClasses.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">Kişi Sayısı</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.applicant_count}
                      onChange={(e) => handleFilterChange('applicant_count', e.target.value)}
                    >
                      <option value="all">Tümü</option>
                      <option value="1">1 kişi</option>
                      <option value="2">2 kişi</option>
                      <option value="3">3 kişi</option>
                      <option value="4+">4+ kişi</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">Randevu Durumu</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.appointment_status}
                      onChange={(e) => handleFilterChange('appointment_status', e.target.value)}
                    >
                      <option value="all">Tümü</option>
                      <option value="has_appointment">Var</option>
                      <option value="no_appointment">Yok</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">Kullanıcı Durumu</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.user_status}
                      onChange={(e) => handleFilterChange('user_status', e.target.value)}
                    >
                      <option value="all">Tümü</option>
                      <option value="active">✅ Aktif</option>
                      <option value="paused">⏸️ Duraklatıldı</option>
                      <option value="inactive">❌ Pasif</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">Tarih Aralığı</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.date_range_status}
                      onChange={(e) => handleFilterChange('date_range_status', e.target.value)}
                    >
                      <option value="all">Tümü</option>
                      <option value="has_ranges">Var</option>
                      <option value="no_ranges">Yok</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Collapse>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-5">
              <FaUsers size={48} className="text-muted mb-3" />
              {users.length === 0 ? (
                <>
                  <h5>Henüz kullanıcı bulunmuyor</h5>
                  <p className="text-muted">İlk kullanıcıyı eklemek için yukarıdaki butonu kullanın.</p>
                </>
              ) : (
                <>
                  <h5>Filtreye uygun kullanıcı bulunamadı</h5>
                  <p className="text-muted">Filtreleri değiştirerek tekrar deneyin.</p>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="d-none d-md-block table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <Table hover className="mb-0">
                <thead style={{ 
                  position: 'sticky', 
                  top: 0, 
                  backgroundColor: '#f8f9fa',
                  zIndex: 10,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  <tr>
                    <th 
                      onClick={() => handleSort('username')}
                      style={{ cursor: 'pointer', userSelect: 'none', padding: '12px 16px' }}
                      className="text-nowrap border-0"
                    >
                      <div className="d-flex align-items-center">
                        Kullanıcı Adı {getSortIcon('username')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('applicant_count')}
                      style={{ cursor: 'pointer', userSelect: 'none', padding: '12px 16px' }}
                      className="text-nowrap border-0"
                    >
                      <div className="d-flex align-items-center">
                        Kişi Sayısı {getSortIcon('applicant_count')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('appointment_city')}
                      style={{ cursor: 'pointer', userSelect: 'none', padding: '12px 16px' }}
                      className="text-nowrap border-0"
                    >
                      <div className="d-flex align-items-center">
                        Şehir {getSortIcon('appointment_city')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('visa_class')}
                      style={{ cursor: 'pointer', userSelect: 'none', padding: '12px 16px' }}
                      className="text-nowrap border-0"
                    >
                      <div className="d-flex align-items-center">
                        Vize {getSortIcon('visa_class')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('appointment_date')}
                      style={{ cursor: 'pointer', userSelect: 'none', padding: '12px 16px' }}
                      className="text-nowrap border-0"
                    >
                      <div className="d-flex align-items-center">
                        Randevu {getSortIcon('appointment_date')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('is_active')}
                      style={{ cursor: 'pointer', userSelect: 'none', padding: '12px 16px' }}
                      className="text-nowrap border-0"
                    >
                      <div className="d-flex align-items-center">
                        Durum {getSortIcon('is_active')}
                      </div>
                    </th>
                    <th className="text-nowrap border-0" style={{ padding: '12px 16px' }}>
                      Tarih Aralığı
                    </th>
                    <th className="border-0 text-end" style={{ padding: '12px 16px' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                                          {paginatedUsers.map((user, index) => {
                    const getBorderColor = () => {
                      if (user.is_active === 1) return '#0d6efd'; // Aktif - Mavi
                      if (user.is_active === 2) return '#ffc107'; // Duraklatıldı - Turuncu
                      return '#dee2e6'; // Pasif - Gri
                    };
                    
                    return (
                    <tr key={user.id} style={{ 
                      borderLeft: `3px solid ${getBorderColor()}`,
                      transition: 'background-color 0.2s'
                    }}>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        <div className="d-flex align-items-center">
                          <div 
                            className="me-2 d-flex align-items-center justify-content-center"
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                              backgroundColor: user.is_active === 1 ? '#e7f1ff' : user.is_active === 2 ? '#fff3cd' : '#f8f9fa',
                              color: user.is_active === 1 ? '#0d6efd' : user.is_active === 2 ? '#ffc107' : '#6c757d',
                              fontWeight: 'bold',
                              fontSize: '0.85rem'
                            }}
                          >
                            <FaUsers size={14} />
                          </div>
                          <div>
                            <div className="fw-medium">{user.username || '-'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        {user.applicant_name ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-applicants-${user.id}`}>
                                <div style={{ textAlign: 'left' }}>
                                  <strong>Başvuranlar:</strong>
                                  <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
                                    {formatApplicantNames(user.applicant_name).map((name, idx) => (
                                      <li key={idx}>{name}</li>
                                    ))}
                                  </ul>
                                </div>
                              </Tooltip>
                            }
                          >
                            <Badge 
                              bg="secondary" 
                              className="fw-normal" 
                              style={{ 
                                padding: '6px 12px',
                                cursor: 'help'
                              }}
                            >
                              {getApplicantCount(user.applicant_name)} kişi
                            </Badge>
                          </OverlayTrigger>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        <span className="text-muted small">{user.appointment_city || '-'}</span>
                      </td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{getVisaClassBadge(user.visa_class)}</td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        {user.appointment_date ? (
                          <div className="d-flex align-items-center gap-1">
                            <FaCalendarAlt size={12} className="text-success" />
                            <small className="text-success fw-medium">
                              {formatDate(user.appointment_date)}
                            </small>
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        <Badge 
                          bg={getUserStatusBadge(user.is_active).bg} 
                          className="fw-normal" 
                          style={{ padding: '6px 12px' }}
                        >
                          {getUserStatusBadge(user.is_active).text}
                        </Badge>
                      </td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        {(user.dateRangeCount && user.dateRangeCount > 0) ? (
                          <Badge bg="primary" className="fw-normal" style={{ padding: '6px 12px' }}>
                            {user.dateRangeCount} adet
                          </Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        <div className="d-flex gap-2 justify-content-end align-items-center">
                          {user.applicant_name && (
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => {
                                // Check if applicant_name is empty
                                if (!user.applicant_name || user.applicant_name.trim() === '') {
                                  alert('⚠️ Kullanıcı bilgileri henüz çekilmedi.\n\nLütfen bilgilerin çekilmesi için bekleyip daha sonra tekrar deneyin.\n\nBu süreç 10 dakika kadar sürebilir.');
                                  return;
                                }
                                setDateRangesUser(user);
                                setDateRanges([]); // Clear previous data
                                setShowDateRangesModal(true);
                                loadUserAppointments(user.id);
                              }}
                              title="Tarih Aralıkları"
                              className="d-flex align-items-center justify-content-center"
                              style={{ width: '32px', height: '32px', padding: '0', border: '1.5px solid' }}
                            >
                              <FaCalendarAlt size={14} />
                            </Button>
                          )}
                          <Button
                            variant={user.is_active === 1 ? "outline-warning" : "outline-success"}
                            size="sm"
                            onClick={() => handleTogglePause(user.id, user.is_active)}
                            title={user.is_active === 1 ? "Duraklat" : "Devam Et"}
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: '32px', height: '32px', padding: '0', border: '1.5px solid' }}
                            disabled={pausingUserId === user.id}
                          >
                            {pausingUserId === user.id ? (
                              <Spinner animation="border" size="sm" style={{ width: '14px', height: '14px' }} />
                            ) : (
                              user.is_active === 1 ? <FaPause size={14} /> : <FaPlay size={14} />
                            )}
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowModal(user)}
                            title="Düzenle"
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: '32px', height: '32px', padding: '0', border: '1.5px solid' }}
                          >
                            <FaEdit size={14} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              setCurrentUser(user);
                              setShowDeleteModal(true);
                            }}
                            title="Sil"
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: '32px', height: '32px', padding: '0', border: '1.5px solid' }}
                          >
                            <FaTrash size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="d-md-none">
              {paginatedUsers.map((user, index) => {
                const getMobileBorderColor = () => {
                  if (user.is_active === 1) return '#0d6efd'; // Aktif - Mavi
                  if (user.is_active === 2) return '#ffc107'; // Duraklatıldı - Turuncu
                  return '#dee2e6'; // Pasif - Gri
                };
                
                return (
                <div 
                  key={user.id} 
                  className="mobile-card"
                  style={{
                    borderLeftColor: getMobileBorderColor()
                  }}
                >
                  {/* Card Header */}
                  <div className="mobile-card-header">
                    <div className="d-flex align-items-center">
                      <div 
                        className="me-2 d-flex align-items-center justify-content-center"
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          backgroundColor: user.is_active === 1 ? '#e7f1ff' : user.is_active === 2 ? '#fff3cd' : '#f8f9fa',
                          color: user.is_active === 1 ? '#0d6efd' : user.is_active === 2 ? '#ffc107' : '#6c757d',
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }}
                      >
                        <FaUsers size={12} />
                      </div>
                      <strong className="text-truncate" style={{ maxWidth: '150px' }}>
                        {user.username || '-'}
                      </strong>
                    </div>
                    <Badge 
                      bg={getUserStatusBadge(user.is_active).bg} 
                      className="ms-2"
                    >
                      {getUserStatusBadge(user.is_active).text}
                    </Badge>
                  </div>

                  {/* Card Body */}
                  <div className="mobile-card-body">
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Kişi Sayısı:</span>
                      <span className="mobile-card-value">
                        {user.applicant_name ? (
                          <Badge bg="secondary">{getApplicantCount(user.applicant_name)} kişi</Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </span>
                    </div>

                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Şehir:</span>
                      <span className="mobile-card-value">{user.appointment_city || '-'}</span>
                    </div>

                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Vize:</span>
                      <span className="mobile-card-value">{getVisaClassBadge(user.visa_class)}</span>
                    </div>

                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Randevu:</span>
                      <span className="mobile-card-value">
                        {user.appointment_date ? (
                          <span className="text-success fw-medium" style={{ fontSize: '0.8125rem' }}>
                            <FaCalendarAlt size={10} className="me-1" />
                            {formatDate(user.appointment_date)}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </span>
                    </div>

                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Durum:</span>
                      <span className="mobile-card-value">
                        <Badge 
                          bg={getUserStatusBadge(user.is_active).bg}
                          className="fw-normal"
                        >
                          {getUserStatusBadge(user.is_active).text}
                        </Badge>
                      </span>
                    </div>

                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Tarih Aralığı:</span>
                      <span className="mobile-card-value">
                        {(user.dateRangeCount && user.dateRangeCount > 0) ? (
                          <Badge bg="primary">{user.dateRangeCount} adet</Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </span>
                    </div>

                    {/* Mobile Actions */}
                    <div className="d-flex gap-2 mt-3 pt-3 border-top flex-wrap">
                      {user.applicant_name && (
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="flex-fill"
                          style={{ border: '1.5px solid' }}
                          onClick={() => {
                            // Check if applicant_name is empty
                            if (!user.applicant_name || user.applicant_name.trim() === '') {
                              alert('⚠️ Kullanıcı bilgileri henüz çekilmedi.\n\nLütfen bilgilerin çekilmesi için bekleyip daha sonra tekrar deneyin.\n\nBu süreç 10 dakika kadar sürebilir.');
                              return;
                            }
                            setDateRangesUser(user);
                            setDateRanges([]);
                            setShowDateRangesModal(true);
                            loadUserAppointments(user.id);
                          }}
                        >
                          <FaCalendarAlt size={12} className="me-1" />
                          Tarihler
                        </Button>
                      )}
                      <Button
                        variant={user.is_active === 1 ? "outline-warning" : "outline-success"}
                        size="sm"
                        className="flex-fill"
                        style={{ border: '1.5px solid' }}
                        onClick={() => handleTogglePause(user.id, user.is_active)}
                        disabled={pausingUserId === user.id}
                      >
                        {pausingUserId === user.id ? (
                          <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px', marginRight: '4px' }} />
                        ) : (
                          user.is_active === 1 ? <FaPause size={12} className="me-1" /> : <FaPlay size={12} className="me-1" />
                        )}
                        {pausingUserId === user.id ? 'Değiştiriliyor...' : (user.is_active === 1 ? 'Duraklat' : 'Devam Et')}
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="flex-fill"
                        style={{ border: '1.5px solid' }}
                        onClick={() => handleShowModal(user)}
                      >
                        <FaEdit size={12} className="me-1" />
                        Düzenle
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        style={{ border: '1.5px solid' }}
                        onClick={() => {
                          setCurrentUser(user);
                          setShowDeleteModal(true);
                        }}
                      >
                        <FaTrash size={12} className="me-1" />
                        Sil
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            {filteredUsers.length > 0 && (
              <div 
                className="d-flex justify-content-between align-items-center p-3 border-top bg-light"
                style={{ 
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 9
                }}
              >
                {/* Items per page selector and user count */}
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-muted fw-medium">Göster:</small>
                    <Form.Select 
                      size="sm" 
                      style={{ width: '70px' }}
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Form.Select>
                    <small className="text-muted">
                      <strong>{filteredUsers.length > 0 ? `${startIndex}-${endIndex}` : '0-0'}</strong> / {filteredUsers.length}
                    </small>
                  </div>
                  <div className="border-start ps-3">
                    <small className="text-muted">
                      <strong>{filteredUsers.length}</strong> / {users.length} kullanıcı
                    </small>
                  </div>
                </div>

                {/* Pagination component */}
                {totalPages > 1 && (
                  <Pagination size="sm" className="mb-0">
                    <Pagination.First 
                      onClick={() => handlePageChange(1)} 
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev 
                      onClick={() => handlePageChange(currentPage - 1)} 
                      disabled={currentPage === 1}
                    />
                    
                    {/* Page numbers */}
                    {(() => {
                      const pages = [];
                      const showPages = 5; // Show 5 page numbers
                      let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                      let endPage = Math.min(totalPages, startPage + showPages - 1);
                      
                      // Adjust start if we're near the end
                      if (endPage - startPage + 1 < showPages) {
                        startPage = Math.max(1, endPage - showPages + 1);
                      }
                      
                      // Add first page and ellipsis if needed
                      if (startPage > 1) {
                        pages.push(
                          <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                            1
                          </Pagination.Item>
                        );
                        if (startPage > 2) {
                          pages.push(<Pagination.Ellipsis key="start-ellipsis" />);
                        }
                      }
                      
                      // Add visible page numbers
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Pagination.Item 
                            key={i} 
                            active={i === currentPage}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </Pagination.Item>
                        );
                      }
                      
                      // Add last page and ellipsis if needed
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(<Pagination.Ellipsis key="end-ellipsis" />);
                        }
                        pages.push(
                          <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                            {totalPages}
                          </Pagination.Item>
                        );
                      }
                      
                      return pages;
                    })()}
                    
                    <Pagination.Next 
                      onClick={() => handlePageChange(currentPage + 1)} 
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last 
                      onClick={() => handlePageChange(totalPages)} 
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                )}
              </div>
            )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {currentUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <h6 className="text-muted mb-3">Giriş Bilgileri</h6>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Kullanıcı Adı *</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    autoComplete="off"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Şifre *</Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      autoComplete="new-password"
                      style={{ paddingRight: '40px' }}
                    />
                    <Button
                      variant="link"
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? "Şifreyi Gizle" : "Şifreyi Göster"}
                      style={{
                        position: 'absolute',
                        right: '5px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        border: 'none',
                        background: 'transparent',
                        padding: '5px 10px',
                        color: '#6c757d',
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#0d6efd'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                    >
                      {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                    </Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vize Tipi *</Form.Label>
                  <Form.Select
                    name="visa_type"
                    value={formData.visa_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Vize tipi seçiniz...</option>
                    <option value="B1/B2">B1/B2</option>
                    <option value="F-1">F-1</option>
                    <option value="J-1">J-1</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Randevu Şehri *</Form.Label>
                  <Form.Select
                    name="appointment_city"
                    value={formData.appointment_city}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Şehir seçiniz...</option>
                    <option value="Ankara">Ankara</option>
                    <option value="Istanbul">Istanbul</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <hr className="my-4" />
            
            {/* Security Questions Language Switch */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-muted mb-0">Güvenlik Soruları</h6>
              <div className="btn-group btn-group-sm" role="group">
                <input 
                  type="radio" 
                  className="btn-check" 
                  name="language" 
                  id="lang-tr" 
                  checked={language === 'tr'}
                  onChange={() => setLanguage('tr')}
                />
                <label className="btn btn-outline-primary" htmlFor="lang-tr">🇹🇷 TR</label>
                
                <input 
                  type="radio" 
                  className="btn-check" 
                  name="language" 
                  id="lang-en" 
                  checked={language === 'en'}
                  onChange={() => setLanguage('en')}
                />
                <label className="btn btn-outline-primary" htmlFor="lang-en">🇺🇸 EN</label>
              </div>
            </div>
            
            <div className="alert alert-info py-2 mb-3">
              <small>
                <strong>Not:</strong> Soruları seçtiğiniz dilde girin ve kullanıcının portal formatıyla aynı olmasına dikkat edin.
              </small>
            </div>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Soru 1</Form.Label>
                  <Form.Select
                    name="question1"
                    value={formData.question1Index >= 0 ? String(formData.question1Index) : ''}
                    onChange={handleQuestionChange}
                  >
                    <option value="">Güvenlik sorusu 1'i seçin</option>
                    {securityQuestions[language]?.question1?.map((question, index) => (
                      <option key={index} value={index}>{question}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cevap 1</Form.Label>
                  <Form.Control
                    type="text"
                    name="answer1"
                    value={formData.answer1}
                    onChange={handleInputChange}
                    placeholder="Cevap 1"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Soru 2</Form.Label>
                  <Form.Select
                    name="question2"
                    value={formData.question2Index >= 0 ? String(formData.question2Index) : ''}
                    onChange={handleQuestionChange}
                  >
                    <option value="">Güvenlik sorusu 2'yi seçin</option>
                    {securityQuestions[language].question2.map((question, index) => (
                      <option key={index} value={index}>{question}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cevap 2</Form.Label>
                  <Form.Control
                    type="text"
                    name="answer2"
                    value={formData.answer2}
                    onChange={handleInputChange}
                    placeholder="Cevap 2"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Soru 3</Form.Label>
                  <Form.Select
                    name="question3"
                    value={formData.question3Index >= 0 ? String(formData.question3Index) : ''}
                    onChange={handleQuestionChange}
                  >
                    <option value="">Güvenlik sorusu 3'ü seçin</option>
                    {securityQuestions[language].question3.map((question, index) => (
                      <option key={index} value={index}>{question}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cevap 3</Form.Label>
                  <Form.Control
                    type="text"
                    name="answer3"
                    value={formData.answer3}
                    onChange={handleInputChange}
                    placeholder="Cevap 3"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              İptal
            </Button>
            <Button variant="primary" type="submit">
              {currentUser ? 'Güncelle' : 'Kaydet'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Kullanıcı Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{currentUser?.username}</strong> kullanıcısını silmek istediğinizden emin misiniz?
          </p>
          <p className="text-muted">
            Bu işlem geri alınamaz ve kullanıcının tüm bilgileri silinecektir.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Sil
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Date Ranges Management Modal */}
      <Modal 
        show={showDateRangesModal} 
        onHide={() => {
          if (!dateRangesLoading) {
            setShowDateRangesModal(false);
            setDateRangesUser(null);
            setEditingDateRange(null);
            setEditDateRange({ start_date: '', end_date: '' });
            setNewDateRange({ start_date: '', end_date: '' });
          }
        }}
        size="lg"
        backdrop={dateRangesLoading ? 'static' : true}
        keyboard={!dateRangesLoading}
      >
        <Modal.Header closeButton style={{
          background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
          color: 'white',
          borderBottom: 'none'
        }}>
          <Modal.Title className="d-flex align-items-center">
            <FaCalendarAlt className="me-3" size={24} />
            <div>
              <div>Tarih Aralıkları Yönetimi</div>
              <small style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                {dateRangesUser?.applicant_name || dateRangesUser?.username}
              </small>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#f8f9fa' }}>
          {/* User Info */}
          <div className="card border-0 shadow-sm mb-4" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            borderRadius: '10px'
          }}>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="d-flex align-items-center">
                    <FaUsers className="me-2 text-primary" size={20} />
                    <div>
                      <small className="text-muted d-block">Kullanıcı</small>
                      <strong>{dateRangesUser?.applicant_name || dateRangesUser?.username}</strong>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-center">
                    <FaCalendarAlt className="me-2 text-success" size={20} />
                    <div>
                      <small className="text-muted d-block">Şehir</small>
                      <strong>{dateRangesUser?.appointment_city}</strong>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-center">
                    <FaCalendarAlt className="me-2 text-info" size={20} />
                    <div>
                      <small className="text-muted d-block">Toplam Aralık</small>
                      <strong>{dateRanges.length} adet</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Loading State for Initial Load */}
          {dateRangesLoading && dateRanges.length === 0 && (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </Spinner>
              <p className="mt-2 text-muted">Tarih aralıkları yükleniyor...</p>
            </div>
          )}

          {/* Calendar View */}
          {!dateRangesLoading && (
            <div className="mb-4" ref={calendarTopRef}>
              <h5 className="mb-3 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <FaCalendarAlt className="me-2 text-primary" />
                  Tarih Aralıkları Takvimi
                  <Badge bg="primary" className="ms-2">{dateRanges.length} aralık</Badge>
                </div>
                <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                  💡 Renkli tarihlere tıklayarak düzenleyin/silin
                </small>
              </h5>
              
              {/* Calendar */}
              <div className="card border-0 shadow-sm mb-3" style={{ backgroundColor: '#fff' }}>
                <div className="card-body p-4">
                  <style>{`
                    .react-calendar {
                      width: 100%;
                      border: none;
                      font-family: inherit;
                    }
                    .react-calendar__tile {
                      padding: 15px 5px;
                      font-size: 0.9rem;
                    }
                    .react-calendar__tile.has-date-range {
                      background: linear-gradient(135deg, #059669 0%, #10b981 100%);
                      color: white;
                      font-weight: 600;
                      border-radius: 4px;
                    }
                    .react-calendar__tile.has-date-range:hover {
                      background: linear-gradient(135deg, #047857 0%, #0d9668 100%);
                    }
                    .react-calendar__tile.has-date-range.selected-range {
                      background: linear-gradient(135deg, #fde047 0%, #facc15 100%) !important;
                      color: #1f2937 !important;
                      box-shadow: 0 0 0 3px rgba(253, 224, 71, 0.4);
                      transform: scale(1.05);
                      z-index: 10;
                      position: relative;
                    }
                    .react-calendar__tile.has-date-range.selected-range:hover {
                      background: linear-gradient(135deg, #fcd34d 0%, #f59e0b 100%) !important;
                    }
                    .react-calendar__tile--active {
                      background: #006edc !important;
                      color: white;
                    }
                    .react-calendar__navigation button {
                      font-size: 1rem;
                      font-weight: 600;
                    }
                    .react-calendar__month-view__weekdays {
                      font-weight: 600;
                      font-size: 0.85rem;
                    }
                  `}</style>
                  
                  <Calendar
                    onChange={handleCalendarDateClick}
                    tileClassName={getTileClassName}
                    locale="tr-TR"
                    showDoubleView={true}
                    activeStartDate={calendarActiveStartDate}
                    onActiveStartDateChange={({ activeStartDate }) => setCalendarActiveStartDate(activeStartDate)}
                  />
                </div>
              </div>

              {/* Selected Range Info & Actions */}
              {selectedRangeForAction && (
                <div className="card border-warning shadow-sm mb-3">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">Seçili Tarih Aralığı</h6>
                        <p className="mb-0 text-muted">
                          <strong>{new Date(selectedRangeForAction.start_date).toLocaleDateString('tr-TR')}</strong>
                          {' - '}
                          <strong>{new Date(selectedRangeForAction.end_date).toLocaleDateString('tr-TR')}</strong>
                          {' '}
                          ({Math.ceil((new Date(selectedRangeForAction.end_date) - new Date(selectedRangeForAction.start_date)) / (1000 * 60 * 60 * 24)) + 1} gün)
                        </p>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            startEditDateRange(selectedRangeForAction);
                            setSelectedRangeForAction(null);
                          }}
                          disabled={dateRangesLoading}
                        >
                          <FaEdit className="me-1" /> Düzenle
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            if (window.confirm('Bu tarih aralığını silmek istediğinizden emin misiniz?')) {
                              removeDateRange(selectedRangeForAction.id);
                              setSelectedRangeForAction(null);
                            }
                          }}
                          disabled={dateRangesLoading}
                        >
                          <FaTrash className="me-1" /> Sil
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setSelectedRangeForAction(null)}
                        >
                          ✕
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Mode */}
              {editingDateRange && (
                <div className="card border-success shadow-sm mb-3">
                  <div className="card-body">
                    <h6 className="mb-3">📝 Tarih Aralığını Düzenle</h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Başlangıç Tarihi</label>
                        <input
                          type="date"
                          className="form-control"
                          value={editDateRange.start_date}
                          onChange={(e) => setEditDateRange(prev => ({...prev, start_date: e.target.value}))}
                          disabled={dateRangesLoading}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Bitiş Tarihi</label>
                        <input
                          type="date"
                          className="form-control"
                          value={editDateRange.end_date}
                          onChange={(e) => setEditDateRange(prev => ({...prev, end_date: e.target.value}))}
                          disabled={dateRangesLoading}
                        />
                      </div>
                      <div className="col-12">
                        <div className="d-flex gap-2 justify-content-end">
                          <Button
                            variant="secondary"
                            onClick={cancelEditDateRange}
                            disabled={dateRangesLoading}
                          >
                            ✕ İptal
                          </Button>
                          <Button
                            variant="success"
                            onClick={saveEditDateRange}
                            disabled={dateRangesLoading}
                          >
                            {dateRangesLoading ? <Spinner size="sm" /> : '✓ Kaydet'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Date Ranges List */}
              {dateRanges.length > 0 && (
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">📋 Tüm Tarih Aralıkları</h6>
                  </div>
                  <div className="card-body p-0">
                    <div className="list-group list-group-flush">
                      {dateRanges.map((range, index) => {
                        const days = Math.ceil((new Date(range.end_date) - new Date(range.start_date)) / (1000 * 60 * 60 * 24)) + 1;
                        return (
                          <div key={range.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                              <Badge bg="primary" className="me-2">#{index + 1}</Badge>
                              <strong>{new Date(range.start_date).toLocaleDateString('tr-TR')}</strong>
                              {' - '}
                              <strong>{new Date(range.end_date).toLocaleDateString('tr-TR')}</strong>
                              <Badge bg="info" className="ms-2">{days} gün</Badge>
                            </div>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                  setSelectedRangeForAction(range);
                                  setCalendarActiveStartDate(new Date(range.start_date));
                                  // Scroll to calendar within modal
                                  setTimeout(() => {
                                    if (calendarTopRef.current) {
                                      calendarTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                  }, 100);
                                }}
                              >
                                <FaCalendarAlt size={12} />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!dateRangesLoading && dateRanges.length === 0 && (
            <div className="text-center py-5 mb-4" style={{
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              borderRadius: '15px',
              border: '2px dashed #6c757d'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📅</div>
              <h5 className="mb-2">Henüz Tarih Aralığı Yok</h5>
              <p className="text-muted mb-0">Aşağıdaki formu kullanarak ilk tarih aralığınızı ekleyin</p>
            </div>
          )}

          {/* Add New Date Range */}
          {!dateRangesLoading && (
          <div className="mb-3" ref={addFormRef}>
            <div className="card border-0 shadow-sm" style={{
              background: 'linear-gradient(135deg, #e7f1ff 0%, #c3dafe 100%)',
              borderRadius: '15px'
            }}>
              <div className="card-header bg-transparent border-0 pt-4 pb-0">
                <h5 className="mb-0 d-flex align-items-center">
                  <FaPlus className="me-2 text-success" />
                  Yeni Tarih Aralığı Ekle
                </h5>
              </div>
              <div className="card-body p-4">
                {/* Hızlı Seçim Butonları */}
                <div className="mb-4">
                  <label className="form-label fw-semibold d-flex align-items-center mb-3">
                    <FaCalendarAlt className="me-2 text-info" />
                    Hızlı Seçim
                  </label>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        const today = new Date();
                        const oneMonthLater = new Date(today);
                        oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
                        setNewDateRange({
                          start_date: today.toISOString().split('T')[0],
                          end_date: oneMonthLater.toISOString().split('T')[0]
                        });
                      }}
                      disabled={dateRangesLoading}
                      style={{ borderRadius: '8px', fontWeight: '500' }}
                    >
                      📅 1 Ay
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        const today = new Date();
                        const threeMonthsLater = new Date(today);
                        threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
                        setNewDateRange({
                          start_date: today.toISOString().split('T')[0],
                          end_date: threeMonthsLater.toISOString().split('T')[0]
                        });
                      }}
                      disabled={dateRangesLoading}
                      style={{ borderRadius: '8px', fontWeight: '500' }}
                    >
                      📅 3 Ay
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        const today = new Date();
                        const sixMonthsLater = new Date(today);
                        sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
                        setNewDateRange({
                          start_date: today.toISOString().split('T')[0],
                          end_date: sixMonthsLater.toISOString().split('T')[0]
                        });
                      }}
                      disabled={dateRangesLoading}
                      style={{ borderRadius: '8px', fontWeight: '500' }}
                    >
                      📅 6 Ay
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setNewDateRange({ start_date: '', end_date: '' });
                      }}
                      disabled={dateRangesLoading}
                      style={{ borderRadius: '8px', fontWeight: '500' }}
                    >
                      ✕ Temizle
                    </Button>
                  </div>
                  <small className="text-muted d-block mt-2">
                    💡 Hızlı bir süre seçebilir veya aşağıdan manuel olarak tarih girebilirsiniz
                  </small>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold d-flex align-items-center">
                      <FaCalendarAlt className="me-2 text-primary" />
                      Başlangıç Tarihi *
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-lg shadow-sm"
                      value={newDateRange.start_date}
                      onChange={(e) => setNewDateRange(prev => ({...prev, start_date: e.target.value}))}
                      min={new Date().toISOString().split('T')[0]}
                      disabled={dateRangesLoading}
                      style={{
                        borderRadius: '10px',
                        border: '2px solid #e9ecef'
                      }}
                    />
                    <small className="text-muted">Randevu başlangıç tarihi</small>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold d-flex align-items-center">
                      <FaCalendarAlt className="me-2 text-danger" />
                      Bitiş Tarihi *
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-lg shadow-sm"
                      value={newDateRange.end_date}
                      onChange={(e) => setNewDateRange(prev => ({...prev, end_date: e.target.value}))}
                      min={newDateRange.start_date || new Date().toISOString().split('T')[0]}
                      disabled={dateRangesLoading}
                      style={{
                        borderRadius: '10px',
                        border: '2px solid #e9ecef'
                      }}
                    />
                    <small className="text-muted">Randevu bitiş tarihi</small>
                  </div>
                </div>
                {newDateRange.start_date && newDateRange.end_date && (
                  <div className="mt-3 p-3 bg-light rounded">
                    <div className="d-flex align-items-center">
                      <Badge bg="info" className="me-2">
                        Seçili Süre: {Math.ceil((new Date(newDateRange.end_date) - new Date(newDateRange.start_date)) / (1000 * 60 * 60 * 24)) + 1} gün
                      </Badge>
                      <small className="text-muted">
                        {new Date(newDateRange.start_date).toLocaleDateString('tr-TR')} - {new Date(newDateRange.end_date).toLocaleDateString('tr-TR')}
                      </small>
                    </div>
                  </div>
                )}
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-success btn-lg w-100 shadow-sm d-flex align-items-center justify-content-center gap-2"
                    onClick={addDateRange}
                    disabled={!newDateRange.start_date || !newDateRange.end_date || dateRangesLoading}
                    style={{
                      borderRadius: '12px',
                      height: '56px',
                      fontWeight: '600',
                      fontSize: '1.1rem',
                      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      border: 'none',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(17, 153, 142, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    {dateRangesLoading ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        <span>Ekleniyor...</span>
                      </>
                    ) : (
                      <>
                        <FaPlus size={18} />
                        <span>Tarih Aralığını Ekle</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          )}

          {!dateRangesLoading && (
          <div className="alert border-0 mt-3" style={{
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            borderRadius: '10px'
          }}>
            <div className="d-flex align-items-center">
              <div className="me-3" style={{ fontSize: '2rem' }}>💡</div>
              <div>
                <strong className="text-primary">Bilgi:</strong>
                <p className="mb-0 text-muted">
                  Bu kullanıcının randevu alabileceği tarih aralıklarını buradan yönetebilirsiniz. 
                  Tarih aralıkları, kullanıcının sistemde randevu oluşturabileceği süreyi belirler.
                </p>
              </div>
            </div>
          </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowDateRangesModal(false);
              setDateRangesUser(null);
              setEditingDateRange(null);
              setEditDateRange({ start_date: '', end_date: '' });
              setNewDateRange({ start_date: '', end_date: '' });
            }}
            disabled={dateRangesLoading}
          >
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Users; 